import axios from 'axios';
import {UnleashClient} from 'unleash-proxy-client';
import * as Sentry from '@sentry/react';
import queryString from 'query-string';
import {observable, action, makeObservable} from 'mobx';
import config from '../config';
import head from 'lodash/head';
import find from 'lodash/find';
import includes from 'lodash/includes';

import {
  LOCAL_STORAGE_KEY_LANGUAGE,
  DEFAULT_LOCALE,
  FEATURE_FLAGS_API_URL,
  NODE_ENV,
  UNLEASH_ENABLED,
  UNLEASH_URL,
  UNLEASH_CLIENT_KEY,
  UNLEASH_APP_NAME,
  UNLEASH_BOOTSTRAP_DEV_FEATURE_FLAGS
} from '../constants';
import {AvailableLanguages, PlatformFlags} from '../api/platform/queries';

import {isSsoOnlyAllowedDomainLoginOption} from '../utils/authentication';
import {DOMAIN_LOGIN_FORBIDDEN_ACTIONS, TOAST_NOTIFICATIONS_TYPES} from '../enums';

class BasePlatformStore {
  constructor(apolloClient, hostname = window.location.hostname) {
    makeObservable(this);
    this.apolloClient = apolloClient;
    this.hostname = hostname;
    this.platformSlug = '';
    this.defaultDomainSlug = '';
    this.navigatorLanguage = this.getNavigatorLanguage();
    this.setLocaleStrings();
    this.translations = {};
    this.isUnleashEnabled = String(UNLEASH_ENABLED).toLowerCase() === 'true';
    if (this.isUnleashEnabled) {
      this.unleashProxyClient = new UnleashClient({
        url: UNLEASH_URL,
        clientKey: UNLEASH_CLIENT_KEY,
        appName: UNLEASH_APP_NAME,
        context: {hostname: hostname, environment: NODE_ENV},
        bootstrapOverride: true,
        bootstrap: UNLEASH_BOOTSTRAP_DEV_FEATURE_FLAGS
      });

      this.unleashProxyClient.on('error', error => Sentry.captureException(error));
      this.unleashProxyClient.on('warn', warning => Sentry.captureMessage(warning));
    }
  }

  @observable developmentFeatureFlags = {};
  @observable developmentFeatureFlagsLoaded = false;
  @observable isContentProtected;
  @observable canAnswerSurveyQuestions;
  @observable canUseMediaInStepHints;
  @observable sso;
  @observable ssoProviders = [];
  @observable isDispatchSso;
  @observable dispatchBaseUrl;
  @observable showStepNoteIconLabel;
  @observable hasFeedbackEnabled;
  @observable hasChecklistsEnabled;
  @observable hasChecklistsSignatureEnabled;
  @observable checklistExtraFieldLabel;
  @observable.ref translations = {};
  @observable availableLanguagesDetails = {};
  @observable domain;
  @observable hasChecklistEscalation;
  @observable loaded = false;
  @observable hasAiStepContentSuggestions;
  @observable hasApiAccess;
  @observable hasDispatchSite;
  @observable hasDispatchEnabled;
  @observable hasDispatchDocumentsSyncEnabled;
  @observable maxVideoUploadMb;

  get hasOptimizedVideos() {
    return Boolean(this.developmentFeatureFlags.optimizedVideos);
  }

  @action setLoaded = loaded => (this.loaded = loaded);

  @action
  setAvailableLanguagesDetails = availableLanguagesDetails => {
    this.availableLanguagesDetails = availableLanguagesDetails;
  };

  // subdomain from the platform db, which will be the platform slug
  setPlatformSlug = subdomain => (this.platformSlug = subdomain);

  setDefaultDomainSlug = domain => (this.defaultDomainSlug = domain && domain.slug);

  getLocalMapping = browserLocale => {
    if (config.availableLanguages.includes(browserLocale)) {
      return this.toDashLocale(browserLocale);
    }
    return this.toDashLocale(DEFAULT_LOCALE);
  };

  getNavigatorLanguage = () => {
    return window.navigator.languages
      ? window.navigator.languages[0]
      : window.navigator.language || window.navigator.userLanguage;
  };

  setLocaleStrings = () => {
    const langFromStorage = localStorage.getItem(LOCAL_STORAGE_KEY_LANGUAGE);
    this.browserLocale =
      (window.location && queryString.parse(window.location.search, {arrayFormat: 'bracket'}).locale) ||
      langFromStorage ||
      this.navigatorLanguage ||
      DEFAULT_LOCALE;
  };

  getActionWhenLoggedInOptionNotAllowed = reason => {
    const {loggedInUserOptions, supportedLoginOptions} = reason;

    const actionsMap = [
      {
        condition: !includes(loggedInUserOptions, 'SSO') && isSsoOnlyAllowedDomainLoginOption(supportedLoginOptions),
        action: DOMAIN_LOGIN_FORBIDDEN_ACTIONS.SHOW_SSO_DIALOG
      },
      {
        condition: true,
        action: DOMAIN_LOGIN_FORBIDDEN_ACTIONS.REDIRECT_AND_LOGOUT
      }
    ];

    return find(actionsMap, 'condition').action;
  };

  fetchAvailableLanguages = () => {
    this.apolloClient.query({fetchPolicy: 'network-only', query: AvailableLanguages}).then(({data}) => {
      const langFromStorage = localStorage.getItem(LOCAL_STORAGE_KEY_LANGUAGE);
      const preferredLocale =
        queryString.parse(window.location.search, {arrayFormat: 'bracket'}).locale ||
        langFromStorage ||
        this.navigatorLanguage ||
        DEFAULT_LOCALE;
      const languageMatchingLocal = lang => {
        return lang.locale.includes(this.toUnderscoreLocale(preferredLocale));
      };
      const languageMatchingLocalShort = lang => {
        return lang.locale.includes(preferredLocale.substring(0, 2));
      };
      const currentLanguage =
        data &&
        data.availableLanguages &&
        (data.availableLanguages.find(languageMatchingLocal) ||
          data.availableLanguages.find(languageMatchingLocalShort) ||
          head(data.availableLanguages));

      if (!langFromStorage) {
        this.setLanguage(this.toDashLocale(currentLanguage.locale));
      }
      this.setCurrentLanguage(this.toDashLocale(currentLanguage.locale));
      this.setAvailablelanguages(data.availableLanguages);
    });
  };

  fetchPlatformFlags = () => {
    return this.apolloClient.query({fetchPolicy: 'network-only', query: PlatformFlags}).then(({data}) => {
      const {platform} = data;
      const {
        isContentProtected,
        canAnswerSurveyQuestions,
        canUseMediaInStepHints,
        sso,
        ssoProviders,
        isDispatchSso,
        dispatchBaseUrl,
        enforceAdminSSO,
        hasFeedbackEnabled,
        hasChecklistsEnabled,
        hasChecklistsSignatureEnabled,
        checklistExtraFieldLabel,
        hasMultidomainDocumentSyncEnabled,
        hasRever,
        domain,
        hasChecklistEscalation,
        hasApiAccess,
        domain: {hasDispatchSite},
        hasDispatchEnabled,
        maxVideoUploadMb
      } = platform;
      this.setPlatformFlags({
        isContentProtected,
        canAnswerSurveyQuestions,
        canUseMediaInStepHints,
        sso,
        ssoProviders,
        isDispatchSso,
        dispatchBaseUrl,
        enforceAdminSSO,
        hasFeedbackEnabled,
        hasChecklistsEnabled,
        hasChecklistsSignatureEnabled,
        checklistExtraFieldLabel,
        hasMultidomainDocumentSyncEnabled,
        hasRever,
        domain,
        hasChecklistEscalation,
        hasApiAccess,
        hasDispatchSite,
        hasDispatchEnabled,
        maxVideoUploadMb
      });

      this.setLoaded(true);
    });
  };

  @action
  setPlatformFlags = ({
    isContentProtected,
    canManageCampaigns,
    hasMultiDomainEnabled,
    canColorContent,
    canUseMediaInStepHints,
    canUseBasicAnalytics,
    canUseCustomFonts,
    sso,
    ssoProviders,
    isDispatchSso,
    dispatchBaseUrl,
    enforceAdminSSO,
    guideApproval,
    name,
    canExportToPdf,
    hasFeedbackEnabled,
    hasBeekeeperIntegrationEnabled,
    hasChecklistsEnabled,
    hasChecklistsSignatureEnabled,
    checklistExtraFieldLabel,
    canUseAutoTranslations,
    hasNativeApp,
    nativeAppAutoRemovalInterval,
    hasChecklistEscalation,
    canAnswerSurveyQuestions,
    domain,
    hasAiStepContentSuggestions,
    hasSkillsEnabled,
    hasConfigurableAlertsRecipients,
    hasApiAccess,
    hasDispatchSite,
    hasDispatchEnabled,
    hasDispatchDocumentsSyncEnabled,
    hasAiGuideFromPdfEnabled,
    hasMultidomainDocumentSyncEnabled,
    maxVideoUploadMb
  }) => {
    this.isContentProtected = isContentProtected;
    this.canManageCampaigns = canManageCampaigns;
    this.hasMultiDomainEnabled = hasMultiDomainEnabled;
    this.canColorContent = canColorContent;
    this.canUseMediaInStepHints = canUseMediaInStepHints;
    this.canUseBasicAnalytics = canUseBasicAnalytics;
    this.canUseCustomFonts = canUseCustomFonts;
    this.sso = sso;
    this.ssoProviders = ssoProviders;
    this.isDispatchSso = isDispatchSso;
    this.dispatchBaseUrl = dispatchBaseUrl;
    this.enforceAdminSSO = enforceAdminSSO;
    this.guideApproval = guideApproval;
    this.name = name;
    this.canExportToPdf = canExportToPdf;
    this.hasFeedbackEnabled = hasFeedbackEnabled;
    this.hasBeekeeperIntegrationEnabled = hasBeekeeperIntegrationEnabled;
    this.hasChecklistsEnabled = hasChecklistsEnabled;
    this.hasChecklistsSignatureEnabled = hasChecklistsSignatureEnabled;
    this.checklistExtraFieldLabel = checklistExtraFieldLabel;
    this.canUseAutoTranslations = canUseAutoTranslations;
    this.hasNativeApp = hasNativeApp;
    this.nativeAppAutoRemovalInterval = nativeAppAutoRemovalInterval;
    this.hasChecklistEscalation = hasChecklistEscalation;
    this.hasSkillsEnabled = hasSkillsEnabled;
    this.hasConfigurableAlertsRecipients = hasConfigurableAlertsRecipients;
    this.canAnswerSurveyQuestions = canAnswerSurveyQuestions;
    this.domain = domain;
    this.hasAiStepContentSuggestions = hasAiStepContentSuggestions;
    this.hasApiAccess = hasApiAccess;
    this.hasDispatchSite = hasDispatchSite;
    this.hasDispatchEnabled = hasDispatchEnabled;
    this.hasDispatchDocumentsSyncEnabled = hasDispatchDocumentsSyncEnabled;
    this.maxVideoUploadMb = maxVideoUploadMb;
    this.hasAiGuideFromPdfEnabled = hasAiGuideFromPdfEnabled;
    this.hasMultidomainDocumentSyncEnabled = hasMultidomainDocumentSyncEnabled;
  };

  fetchDevelopmentFeatureFlags = async () => {
    let data;

    const formatToggles = toggles =>
      toggles.reduce((acc, toggle) => {
        acc[toggle.name] = toggle.enabled;
        return acc;
      }, {});

    try {
      if (this.isUnleashEnabled) {
        await this.unleashProxyClient.start();

        // fetch all toggles based on the hostname context configuration
        let allToggles = await this.unleashProxyClient.getAllToggles();
        // Default to bootstrap flags if unleash is reachable but no feature toggles are available
        if (allToggles.length < 1) {
          allToggles = UNLEASH_BOOTSTRAP_DEV_FEATURE_FLAGS;
        }

        data = formatToggles(allToggles);
      } else {
        const response = await axios.get(FEATURE_FLAGS_API_URL);
        data = response.data;
      }
    } catch (error) {
      data = formatToggles(UNLEASH_BOOTSTRAP_DEV_FEATURE_FLAGS);
      Sentry.captureException(new Error('Could not fetch development feature flags from Unleash server'));
    }

    this.setDevelopmentFeatureFlags(data);
  };

  @action
  setDevelopmentFeatureFlags = data => {
    this.developmentFeatureFlags = data;
    this.developmentFeatureFlagsLoaded = true;
  };

  getLanguage = () => {
    return this.toUnderscoreLocale(this.currentLanguage);
  };

  setLocalLanguage = language => {
    localStorage.setItem(LOCAL_STORAGE_KEY_LANGUAGE, language);
  };

  setLanguage = language => {
    this.setLocalLanguage(language);
    window.location = this.getLocationWithoutLocale();
  };

  setOrganization = organization => {
    return (this.organization = organization);
  };

  getOrganization = () => this.organization;

  setOrganizationName = name => {
    return (this.organizationName = name);
  };

  getLocationWithoutLocale = () => {
    const filtered = queryString.stringify({
      ...queryString.parse(window.location.search),
      ...{locale: undefined}
    });
    const base = window.location.href.split('?')[0];

    return filtered ? `${base}?${filtered}` : base;
  };

  toDashLocale(locale = '') {
    return locale.replace('_', '-');
  }

  toUnderscoreLocale(locale = '') {
    return locale.replace('-', '_');
  }

  setStore = store => (this.store = store);

  @action
  setTranslations = translations => (this.translations = translations);

  handleGoesOffline = () => {
    const {
      toastNotifications: {addNotification}
    } = this.store;

    addNotification({
      message: this.translations.goesOffline,
      type: TOAST_NOTIFICATIONS_TYPES.ERROR
    });
  };

  handleGoesBackOnline = () => {
    const {
      toastNotifications: {addNotification}
    } = this.store;

    addNotification({
      message: this.translations.goesBackOnline,
      type: TOAST_NOTIFICATIONS_TYPES.SUCCESS
    });
  };

  addOnlineStatusListener = () => {
    window.addEventListener('offline', this.handleGoesOffline);
    window.addEventListener('online', this.handleGoesBackOnline);
  };

  get isEmbeddedV2() {
    return this.store.router?.currentRoute?.path.includes('embed-v2');
  }
}

export default BasePlatformStore;
